import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { withError } from '@/react-tools/react-query';
import axios, { parseAxiosError } from '@/portainer/services/axios/axios';

import { SilencedAlertsResponse } from './types';

const queryKeys = {
  base: () => ['alerts', 'silenced'],
};

export function useSilencedAlerts<T = SilencedAlertsResponse>({
  select,
  refetchInterval,
}: {
  select?: (data: SilencedAlertsResponse) => T;
  refetchInterval?: number;
}) {
  return useQuery(queryKeys.base(), async () => getSilencedAlerts(), {
    ...withError('Unable to get silenced alerts'),
    select,
    refetchInterval,
  });
}

export async function getSilencedAlerts() {
  try {
    const { data } = await axios.get<SilencedAlertsResponse>(
      `/observability/alerting/alerts`,
      {
        params: { status: 'silenced' },
      }
    );
    return data;
  } catch (error) {
    throw parseAxiosError(error);
  }
}

export async function deleteSilence(
  silenceId: string,
  alertManagerURL: string = 'localhost:9093'
) {
  try {
    const { data } = await axios.delete(
      `/observability/alerting/silence/${silenceId}?alertManagerURL=${encodeURIComponent(
        alertManagerURL
      )}`
    );
    return data;
  } catch (error) {
    throw parseAxiosError(error);
  }
}

export function useDeleteSilence() {
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      silenceId,
      alertManagerURL,
    }: {
      silenceId: string;
      alertManagerURL?: string;
    }) => deleteSilence(silenceId, alertManagerURL),
    {
      onSuccess: () => {
        // Invalidate silenced alerts query to refresh the UI
        queryClient.invalidateQueries(queryKeys.base());
      },
      ...withError('Failed to delete silence'),
    }
  );
}

export type SilencedAlertRowData = {
  startsAt: Date;
  endsAt: Date;
  createdBy: string;
  comment: string;
  alertRule: string;
};
